/**
 * 角色卡选择控件（T24）。
 *
 * 会话参数菜单里的角色卡选择器：列出全部角色卡（`presetRepo.listPresets()`）
 * 并额外提供「不使用角色卡」。选择结果由上层写入
 * `conversationRepo.updateConversation(id, { presetId })`；本组件只负责「可见 + 可访问
 * + 登记元数据」。
 */

import type { ReactElement } from 'react';

import type { Preset } from '../../core/types';
import { isStandaloneWorldBook } from '../presets/assemble';
import { useComponentMeta } from '../../ui';
import { chatComponentMeta } from './componentMeta';
import styles from './ChatControlsDrawer.module.css';

/** 「不使用角色卡」选项的固定文案。 */
export const NO_PRESET_LABEL = '不使用角色卡';

export interface PresetSelectorProps {
  /** 候选角色卡（来自 `presetRepo.listPresets()`）。 */
  presetList: readonly Preset[];
  /** 当前会话绑定的角色卡 id；null 表示不使用角色卡。 */
  selectedId: string | null;
  /** 没有活动会话时禁用入口。 */
  disabled?: boolean;
  onSelect: (presetId: string | null) => void;
}

export function PresetSelector({
  presetList,
  selectedId,
  disabled,
  onSelect,
}: PresetSelectorProps): ReactElement {
  const selectRef = useComponentMeta<HTMLSelectElement>(chatComponentMeta('chat.controls.presetSelect'));
  return (
    <label className={styles.field}>
      角色卡
      <select ref={selectRef} data-ui-id="chat.controls.presetSelect" aria-label="角色卡选择"
        value={selectedId ?? ''} disabled={disabled ?? false}
        onChange={(event) => onSelect(event.currentTarget.value || null)}>
        <option value="">{NO_PRESET_LABEL}</option>
        {presetList.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {isStandaloneWorldBook(preset) ? `${preset.name}（世界书）` : preset.name}
          </option>
        ))}
      </select>
    </label>
  );
}
